"use client";

import type React from "react";
import { useState, useEffect, useRef } from "react";
import {
  ChevronRight,
  Menu,
  X,
  LogOut,
  User,
  Building2,
  Briefcase, // Agency Icon
  Settings,
  UserCircle,
  ChevronDown,
  LogIn,
} from "lucide-react";
import { AdminNotificationPanel } from "./AdminNotificationPanel";

interface NavigationItem {
  kind?: "section" | "divider";
  segment?: string;
  path?: string;
  title: string;
  icon?: React.ReactNode;
  children?: NavigationItem[];
  badge?: string;
}

interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

interface ProfessionalSidebarProps {
  navigation: NavigationSection[];
  currentPath: string;
  onNavigate: (path: string) => void;
  user?: {
    name: string;
    email: string;
    image?: string;
    role?: string;
    accountType?: string; // Ensure this is passed from parent
  };
  onSignOut: () => void;
  className?: string;
}

export function ProfessionalSidebar({
  navigation,
  currentPath,
  onNavigate,
  user,
  onSignOut,
  className = "",
}: ProfessionalSidebarProps) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isProfileMenuOpen]);

  // Auto-expand items based on current path
  useEffect(() => {
    const newExpanded = new Set<string>();
    navigation.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children) {
          const hasActiveChild = item.children.some((child) => {
            const childPath = child.path || `/${child.segment || ""}`;
            return (
              currentPath === childPath ||
              currentPath.startsWith(childPath + "/")
            );
          });
          if (hasActiveChild) {
            newExpanded.add(item.title);
          }
        }
      });
    });
    setExpandedItems(newExpanded);
  }, [currentPath, navigation]);

  const toggleExpanded = (title: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(title)) {
      newExpanded.delete(title);
    } else {
      newExpanded.add(title);
    }
    setExpandedItems(newExpanded);
  };

  const isActive = (item: NavigationItem) => {
    const itemPath = item.path || `/${item.segment || ""}`;

    if (item.children) {
      const hasExactActiveChild = item.children.some((child) => {
        const childPath = child.path || `/${child.segment || ""}`;
        return currentPath === childPath;
      });
      return currentPath === itemPath || hasExactActiveChild;
    }

    return currentPath === itemPath;
  };

  const isChildActive = (item: NavigationItem) => {
    const itemPath = item.path || `/${item.segment || ""}`;
    return currentPath === itemPath;
  };

  const handleNavigation = (item: NavigationItem) => {
    const path = item.path || `/${item.segment || ""}`;
    if (path && path !== "/") {
      onNavigate(path);
      setIsMobileOpen(false);
    }
  };

  // Helper to check for specific nav items
  const hasNavigationItem = (title: string) => {
    return navigation.some((section) =>
      section.items.some((item) => item.title === title),
    );
  };

  const detectRoleFromNavigation = () => {
    if (!navigation || navigation.length === 0) return "Portal";

    const hasAdminSection = navigation.some(
      (section) => section.title === "Admin",
    );

    // Check for Agency specific item
    const hasEmployerManagement = hasNavigationItem("Employer Management");

    const hasClientUserGroups = hasNavigationItem("Group Management");

    const hasModeration = navigation.some(
      (section) => section.title === "Moderation",
    );
    const hasContentOnly = navigation.some(
      (section) => section.title === "Content" && !hasAdminSection,
    );
    const hasSuperAdmin = navigation.some(
      (section) => section.title === "SuperAdmin",
    );

    if (hasAdminSection) return "Admin Portal";
    if (hasEmployerManagement) return "HR Agency Portal"; // 👈 Priority check for Agency
    if (hasClientUserGroups) return "Client Portal";
    if (hasModeration) return "Moderator Portal";
    if (hasSuperAdmin) return "Super Admin Portal";
    if (hasContentOnly) return "Author Portal";

    return "Portal";
  };

  const getPortalInfo = () => {
    const defaultInfo = { title: detectRoleFromNavigation(), icon: Building2 };

    if (!user?.role) {
      return defaultInfo;
    }

    const role = user.role.toLowerCase().trim();
    const accountType = user.accountType?.toLowerCase().trim();

    switch (role) {
      case "admin":
        return { title: "Admin Portal", icon: Settings };

      case "client":
        // 1. Check User Data (Best Source)
        if (accountType === "standard") {
          return { title: "HR Agency Portal", icon: Briefcase };
        }

        // 2. Fallback: Check Navigation (If user data is missing accountType)
        // Agencies have "Employer Management" in their sidebar
        if (hasNavigationItem("Employer Management")) {
          return { title: "HR Agency Portal", icon: Briefcase };
        }

        return { title: "Client Portal", icon: Building2 };

      case "author":
        return { title: "Author Portal", icon: UserCircle };
      case "moderator":
        return { title: "Moderator Portal", icon: User };
      case "student":
        return { title: "Student Portal", icon: User };
      default:
        return defaultInfo;
    }
  };

  const portalInfo = getPortalInfo(); // Calculate once

  const handleProfileMenuClick = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const handleSignOut = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // 1. Identify if the user is an HR Agency
    const isAgency =
      user?.role?.toLowerCase() === "client" &&
      user?.accountType?.toLowerCase() === "standard";

    // 2. Clear session data (triggers your existing logout logic)
    onSignOut();

    // 3. Perform the conditional redirect
    if (isAgency) {
      onNavigate("/email-login"); // Agency specific route
    } else {
      onNavigate("/login"); // Default route for everyone else
    }

    setTimeout(() => {
      setIsProfileMenuOpen(false);
    }, 10);
  };

  const handleProfileNavigation = (path: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsProfileMenuOpen(false);
    onNavigate(path);
  };

  const renderNavigationItem = (item: NavigationItem, isChild = false) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.title);
    const itemIsActive = isActive(item);
    const childIsActive = isChildActive(item);
    const itemKey = `${item.title}-${item.segment || item.path}`;

    return (
      <div key={itemKey} className="w-full">
        <button
          onClick={() => {
            if (hasChildren) {
              toggleExpanded(item.title);
            } else {
              handleNavigation(item);
            }
          }}
          className={`
            w-full flex items-center justify-between text-left transition-all duration-200 ease-in-out group relative
            ${isChild ? "px-3 py-2 ml-4 text-xs rounded-lg" : "px-3 py-2.5 text-xs rounded-lg mx-1.5 my-0.5"}
            ${
              itemIsActive || childIsActive
                ? isChild
                  ? "bg-gradient-to-r from-[#007A5F]/15 to-[#007A5F]/5 text-[#007A5F] font-bold border-l-3 border-[#007A5F] shadow-sm"
                  : "bg-gradient-to-r from-[#007A5F]/20 to-[#007A5F]/10 text-[#007A5F] font-bold shadow-md border border-[#007A5F]/20"
                : "text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/50 hover:text-[#007A5F] hover:shadow-sm"
            }
            transform active:scale-[0.99]
          `}
        >
          {(itemIsActive || childIsActive) && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full animate-[shimmer_3s_infinite] opacity-30"></div>
          )}

          <div className="flex items-center space-x-2.5 min-w-0 flex-1 relative z-10">
            {item.icon && (
              <div
                className={`
                flex-shrink-0 transition-all duration-200 ease-in-out transform
                ${
                  itemIsActive || childIsActive
                    ? "text-[#007A5F] scale-105"
                    : "text-gray-500 group-hover:text-[#007A5F]"
                }
              `}
              >
                {item.icon}
              </div>
            )}
            <span className="font-medium truncate transition-all duration-200">
              {item.title}
            </span>
            {item.badge && (
              <span className="px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full flex-shrink-0 animate-pulse">
                {item.badge}
              </span>
            )}
          </div>

          {hasChildren && (
            <div
              className={`
              transition-all duration-200 ease-in-out flex-shrink-0 ml-2 transform relative z-10
              ${isExpanded ? "rotate-90 text-[#007A5F]" : "text-gray-400 group-hover:text-[#007A5F]"}
            `}
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </div>
          )}

          {(itemIsActive || childIsActive) && !isChild && (
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-[#007A5F] rounded-l-full shadow-md"></div>
          )}
        </button>

        {hasChildren && (
          <div
            className={`
            overflow-hidden transition-all duration-500 ease-in-out
            ${isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}
          `}
          >
            <div className="pt-1 pb-0.5 space-y-0.5">
              {item.children?.map((child) => renderNavigationItem(child, true))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-white shadow-2xl border-r border-gray-100 w-full">
      <div className="relative p-4 border-b border-gray-200 bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#007A5F]/10 to-transparent rounded-full transform translate-x-16 -translate-y-16"></div>
        <div className="absolute bottom-0 left-0 w-20 h-20 from-[#007A5F]/8 to-transparent rounded-full transform -translate-x-8 translate-y-8"></div>

        <div className="relative z-10">
          <div className="flex items-center mb-4">
            {" "}
            {/* removed justify-between */}
            <div className="transition-transform duration-300 hover:scale-105">
              <img
                src="/CodeChatura_logo.png"
                alt="CodeChatura Logo"
                className="w-36 h-10 object-contain drop-shadow-md"
              />
            </div>
          </div>

          {/* Portal info row */}
          <div className="flex items-center justify-between space-x-2">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-gradient-to-br from-[#007A5F] to-[#005A47] rounded-lg flex items-center justify-center shadow-sm">
                <portalInfo.icon className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-700 tracking-wide">
                  {portalInfo.title}
                </p>
                <div className="w-12 h-0.5 bg-gradient-to-r from-[#007A5F] to-[#005A47] rounded-full mt-0.5" />
              </div>
            </div>

            {/* Bell sits inline with portal title row */}
            <AdminNotificationPanel userRole={user?.role} />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400 scrollbar-thumb-rounded-full">
        <nav className="py-3 px-1.5">
          {navigation.map((section, sectionIndex) => (
            <div key={`section-${sectionIndex}`} className="mb-4">
              <div className="px-3 py-1.5 mb-2">
                <h3 className="text-xs font-bold text-[#007A5F]/80 uppercase tracking-widest flex items-center">
                  <span className="flex-1">{section.title}</span>
                  <div className="flex-1 h-px bg-gradient-to-r from-[#007A5F]/30 to-transparent ml-2.5"></div>
                </h3>
              </div>
              <div className="space-y-0.5">
                {section.items.map((item) => renderNavigationItem(item))}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {user ? (
        <div
          className="border-t border-[#007A5F]/20 p-2.5 bg-[#007A5F] relative"
          ref={profileMenuRef}
        >
          <button
            onClick={handleProfileMenuClick}
            className="w-full flex items-center space-x-2.5 p-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 shadow-sm hover:shadow-md hover:bg-white/15 transition-all duration-300 group"
          >
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0 shadow-md border border-white/30">
              {user.image ? (
                <img
                  src={user.image || "/placeholder.svg"}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover border-2 border-white/50"
                />
              ) : (
                <User className="w-3.5 h-3.5 text-white" />
              )}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-semibold text-white truncate">
                {user.name}
              </p>
              <p className="text-xs text-white/70 truncate">{user.email}</p>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-white/70 transition-transform duration-200 ${isProfileMenuOpen ? "rotate-180" : ""}`}
            />
          </button>

          {isProfileMenuOpen && (
            <div className="absolute bottom-full left-2.5 right-2.5 mb-2 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 animate-in slide-in-from-bottom-2 duration-200">
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => handleProfileNavigation("/profile", e)}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center space-x-2"
              >
                <UserCircle className="w-4 h-4" />
                <span>View Profile</span>
              </button>
              <div className="border-t border-gray-200 my-1"></div>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={handleSignOut}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors duration-200 flex items-center space-x-2"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="border-t border-[#007A5F]/20 p-2.5 bg-[#007A5F] relative">
          <button
            onClick={() => onNavigate("/login")}
            className="group relative overflow-hidden bg-gradient-to-r from-[#007A5F] via-[#00A37A] to-[#007A5F] hover:from-[#00A37A] hover:via-[#007A5F] hover:to-[#00A37A] text-white px-6 py-3 rounded-2xl font-semibold shadow-2xl hover:shadow-[#007A5F]/25 transform hover:scale-105 hover:-translate-y-1 transition-all duration-300 w-full flex items-center justify-center space-x-2.5"
          >
            <div className="flex items-center gap-2.5">
              <LogIn className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
              <span className="text-sm">Login</span>
              <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
            </div>
            <div className="absolute inset-0 -top-1 -left-1 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 group-hover:animate-pulse"></div>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <button
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-3 bg-gradient-to-r from-[#007A5F] to-[#005A47] text-white rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 active:scale-95 backdrop-blur-sm border border-white/20"
      >
        <Menu className="w-5 h-5" />
      </button>

      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40 transition-all duration-300"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <div
        className={`hidden lg:flex flex-col w-[272px] flex-shrink-0 ${className}`}
      >
        {sidebarContent}
      </div>

      <div
        className={`
        lg:hidden fixed inset-y-0 left-0 z-50 w-[260px] transform transition-all duration-500 ease-in-out
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
      `}
      >
        <div className="relative h-full">
          <button
            onClick={() => setIsMobileOpen(false)}
            className="absolute top-4 right-4 z-50 p-2 text-gray-600 hover:text-white hover:bg-red-500 rounded-lg transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-xl cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
          {sidebarContent}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes slide-in-from-bottom-2 {
          from { transform: translateY(8px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-in { animation-fill-mode: both; }
        .slide-in-from-bottom-2 { animation-name: slide-in-from-bottom-2; }
        .duration-200 { animation-duration: 200ms; }
      `}</style>
    </>
  );
}
